import React from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLink, faArrowRight, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import RouteSeo from '../components/RouteSeo';
import ToolContent from '../components/ToolContent';
import ToolLayout from '../components/ToolLayout';
import { URL_ABOUT } from '../content/toolGuides';
import { URL_ENCODING_ROUTE } from '../content/tools';
import { useTextConverter } from '../hooks/useTextConverter';

const UrlEncoding: React.FC = () => {
  const converter = useTextConverter({
    modes: ['encode', 'decode'] as const,
    transforms: { encode: encodeURIComponent, decode: decodeURIComponent },
    errors: {
      encode: 'Failed to encode URL.',
      decode: 'Failed to decode. Make sure your input is valid URL-encoded text.',
    },
  });
  const { mode } = converter;

  return (
    <>
      <RouteSeo route={URL_ENCODING_ROUTE} />
      <ToolLayout
        title="URL Encoding / Decoding"
        description="Encode or decode text for use in URLs"
        icon={faLink}
        controls={
          <div className="tool-controls__group">
            <Form.Check
              type="radio"
              id="encode-radio-url"
              label="Encode"
              checked={mode === 'encode'}
              onChange={() => {
                converter.setMode('encode');
                converter.clearError();
              }}
              inline
            />
            <Form.Check
              type="radio"
              id="decode-radio-url"
              label="Decode"
              checked={mode === 'decode'}
              onChange={() => {
                converter.setMode('decode');
                converter.clearError();
              }}
              inline
            />
          </div>
        }
        error={converter.error}
        onDismissError={converter.clearError}
        inputLabel={mode === 'encode' ? 'Plain Text' : 'URL-Encoded Text'}
        inputValue={converter.input}
        onInputChange={converter.setInput}
        inputPlaceholder={mode === 'encode' ? 'Enter text to encode...' : 'Enter URL-encoded text to decode...'}
        actions={
          <>
            <Button variant="primary" onClick={converter.convert} disabled={!converter.input}>
              <FontAwesomeIcon icon={faArrowRight} className="me-2" />
              {mode === 'encode' ? 'Encode' : 'Decode'}
            </Button>
            <Button variant="outline-secondary" onClick={converter.switchMode} disabled={!converter.output}>
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Switch
            </Button>
          </>
        }
        outputLabel={mode === 'encode' ? 'URL-Encoded Text' : 'Plain Text'}
        outputValue={converter.output}
        onCopy={converter.copy}
        copied={converter.copied}
        aboutTitle="About URL Encoding"
        about={URL_ABOUT}
      >
        <ToolContent route={URL_ENCODING_ROUTE} />
      </ToolLayout>
    </>
  );
};

export default UrlEncoding;
